import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormSection,
  Input,
  PageShell,
  RadioGroup,
  RadixSelect,
  SectionCard,
  Skeleton,
  Switch,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import {
  useMileageEarnPolicyMutation,
  useMileageEarnPolicyQuery,
} from '../hooks/useMileageEarnPoliciesQuery';
import type { MileageMinPurchaseBasis,
  MileageEarnBasis,
  MileageEarnPolicyCreatePayload,
  MileageEarnPolicyUpdatePayload,
  MileageEarnType,
  MileagePayoutMode,
  MileagePolicyScope,
  MileageUseMaxType,
  MileageValidityUnit,
} from '../types';

interface FormState {
  name: string;
  scope: MileagePolicyScope;
  earnType: MileageEarnType;
  earnValue: string;
  earnBasis: MileageEarnBasis;
  validityValue: string;
  validityUnit: MileageValidityUnit;
  // 지급시점 (고도몰)
  payoutMode: MileagePayoutMode;
  payoutDelayDays: string;
  // 적립 제외 (PRODUCT scope)
  earnExcluded: boolean;
  active: boolean;
  // DEFAULT 전용
  reearnOnMileagePayment: boolean;
  minBalanceToUse: string;
  useMinPerOrder: string;
  useMinPurchaseAmount: string;
  useMinPurchaseBasis: MileageMinPurchaseBasis;
  useMaxType: MileageUseMaxType | '';
  useMaxValue: string;
  truncationDigits: string;
}

const EMPTY: FormState = {
  name: '',
  scope: 'DEFAULT',
  earnType: 'RATE',
  earnValue: '',
  earnBasis: 'SALE',
  validityValue: '',
  validityUnit: 'MONTH',
  payoutMode: 'IMMEDIATE',
  payoutDelayDays: '',
  earnExcluded: false,
  active: true,
  reearnOnMileagePayment: false,
  minBalanceToUse: '',
  useMinPerOrder: '',
  useMinPurchaseAmount: '',
  useMinPurchaseBasis: 'EXCLUDE_DISCOUNT',
  useMaxType: '',
  useMaxValue: '',
  truncationDigits: '',
};

const SCOPE_OPTIONS = [
  { value: 'DEFAULT', label: '전역 기본' },
  { value: 'PRODUCT', label: '상품별' },
];
const EARN_TYPE_OPTIONS = [
  { value: 'RATE', label: '비율' },
  { value: 'FIXED', label: '정액' },
];
const BASIS_OPTIONS = [
  { value: 'SALE', label: '판매가' },
  { value: 'SUPPLY', label: '공급가' },
];
const VALIDITY_UNIT_OPTIONS = [
  { value: 'DAY', label: '일' },
  { value: 'MONTH', label: '개월' },
];
// value 를 MileagePayoutMode 로 타이핑해 enum 문자열 오타를 컴파일 타임에 잡는다(BE 바이트 일치 보호).
const PAYOUT_MODE_OPTIONS: ReadonlyArray<{ value: MileagePayoutMode; label: string }> = [
  { value: 'IMMEDIATE', label: '결제확정 즉시 지급' },
  { value: 'AFTER_PAYMENT_DAYS', label: '결제확정 후 N일 지급' },
  { value: 'AFTER_DELIVERY_DAYS', label: '배송완료 후 N일 지급' },
  // 구매확정 시 적립 — OrderPolicySettings.benefitGrantTiming='PURCHASE_CONFIRM'(#22)과
  // 동일하게 "구매확정 적립"을 가리킨다(근거: 게이트 §2-2/02.png, 고도몰 기본). N일 지연 없음.
  { value: 'AFTER_PURCHASE_CONFIRM', label: '구매확정 시' },
];

// 지연 일수(N일)가 적용되지 않는 지급 시점 모드. 이 모드들은 delay 입력을 숨기고 payoutDelayDays=0
// 으로 전송한다(IMMEDIATE 와 동일 UX). AFTER_*_DAYS 만 지연 일수가 유의미하다.
const isImmediatePayout = (mode: MileagePayoutMode): boolean =>
  mode === 'IMMEDIATE' || mode === 'AFTER_PURCHASE_CONFIRM';

// 최소 적립 단위 ↔ 절사 자릿수(truncationDigits) 매핑. 와이어는 digits(정수) 그대로 전송.
// digits=0 → 1원 단위(절사 없음), 1 → 10원, 2 → 100원, 3 → 1000원 단위 FLOOR.
const MIN_EARN_UNIT_OPTIONS = [
  { value: '0', label: '1원 단위 (절사 없음)' },
  { value: '1', label: '10원 단위' },
  { value: '2', label: '100원 단위' },
  { value: '3', label: '1,000원 단위' },
];
/** QA-436 — 고도몰 "할인금액 미포함 가격기준 / 할인금액 포함 가격기준". */
const MIN_PURCHASE_BASIS_OPTIONS = [
  { value: 'EXCLUDE_DISCOUNT', label: '할인 전 금액 기준' },
  { value: 'INCLUDE_DISCOUNT', label: '할인 후 금액 기준' },
] as const;

const USE_MAX_TYPE_OPTIONS = [
  { value: '', label: '미설정' },
  { value: 'FIXED', label: '주문당 정액 상한' },
  { value: 'PERCENT', label: '주문금액 대비 % 상한' },
];

const toNumberOrNull = (s: string): number | null => {
  const v = Number(s);
  return s.trim() !== '' && Number.isFinite(v) ? Math.floor(v) : null;
};

// RATE earnValue 는 wire 에서 basis points (10000 = 100%). 운영자에게는 퍼센트로 보여준다.
const PERCENT_TO_BASIS_POINTS = 100;

const percentToBasisPoints = (percent: number): number =>
  Math.round(percent * PERCENT_TO_BASIS_POINTS);

const basisPointsToPercentText = (basisPoints: number): string =>
  String(basisPoints / PERCENT_TO_BASIS_POINTS);

export const EarnPolicyFormPage = () => {
  const navigate = useNavigate();
  const { policyId: param } = useParams<{ policyId: string }>();
  const isNew = param === 'new' || param == null;
  const policyId = isNew ? null : param!;

  const query = useMileageEarnPolicyQuery(policyId);
  const { create, update, softDelete } = useMileageEarnPolicyMutation();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!query.data) return;
    const p = query.data;
    setForm({
      name: p.name,
      scope: p.scope,
      earnType: p.earnType,
      earnValue: p.earnType === 'RATE' ? basisPointsToPercentText(p.earnValue) : String(p.earnValue),
      earnBasis: p.earnBasis,
      validityValue: String(p.validityValue),
      validityUnit: p.validityUnit,
      payoutMode: p.payoutMode ?? 'IMMEDIATE',
      payoutDelayDays: p.payoutDelayDays != null ? String(p.payoutDelayDays) : '',
      earnExcluded: p.earnExcluded ?? false,
      active: p.active,
      reearnOnMileagePayment: p.reearnOnMileagePayment ?? false,
      minBalanceToUse: p.minBalanceToUse != null ? String(p.minBalanceToUse) : '',
      useMinPerOrder: p.useMinPerOrder != null ? String(p.useMinPerOrder) : '',
      useMinPurchaseAmount: p.useMinPurchaseAmount != null ? String(p.useMinPurchaseAmount) : '',
      useMinPurchaseBasis: p.useMinPurchaseBasis ?? 'EXCLUDE_DISCOUNT',
      useMaxType: p.useMaxType ?? '',
      useMaxValue: p.useMaxValue != null ? String(p.useMaxValue) : '',
      truncationDigits: p.truncationDigits != null ? String(p.truncationDigits) : '',
    });
  }, [query.data]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const isDefault = form.scope === 'DEFAULT';

  const earnValueNumber = Number(form.earnValue);
  const earnValuePreview =
    form.earnValue.trim() === '' || !Number.isFinite(earnValueNumber)
      ? form.earnType === 'RATE'
        ? '적립률을 % 단위로 입력하세요. (예: 5 입력 → 결제금액의 5% 적립)'
        : '적립할 금액을 원 단위로 입력하세요. (예: 1000 입력 → 1,000원 적립)'
      : form.earnType === 'RATE'
        ? `결제금액의 ${earnValueNumber}% 가 적립됩니다.`
        : `주문당 ${Math.floor(earnValueNumber).toLocaleString('ko-KR')}원이 적립됩니다.`;

  // 지급시점/적립제외 (모든 scope 공통). AFTER_*_DAYS 모드만 지연일수 의미.
  const buildPayoutFields = () => ({
    payoutMode: form.payoutMode,
    payoutDelayDays: isImmediatePayout(form.payoutMode)
      ? 0
      : (toNumberOrNull(form.payoutDelayDays) ?? 0),
    earnExcluded: form.earnExcluded,
  });

  const buildDefaultFields = () =>
    isDefault
      ? {
          reearnOnMileagePayment: form.reearnOnMileagePayment,
          minBalanceToUse: toNumberOrNull(form.minBalanceToUse),
          useMinPerOrder: toNumberOrNull(form.useMinPerOrder),
          useMinPurchaseAmount: toNumberOrNull(form.useMinPurchaseAmount),
          useMinPurchaseBasis: form.useMinPurchaseBasis,
          useMaxType: form.useMaxType === '' ? null : form.useMaxType,
          useMaxValue: toNumberOrNull(form.useMaxValue),
          truncationDigits: toNumberOrNull(form.truncationDigits),
          truncationMode: 'FLOOR' as const,
        }
      : {};

  const handleSubmit = async () => {
    setError(null);
    const earnValue = Number(form.earnValue);
    const validityValue = Number(form.validityValue);
    if (form.name.trim() === '') {
      setError('정책명을 입력하세요.');
      return;
    }
    if (!Number.isFinite(earnValue) || earnValue <= 0) {
      setError('적립값은 1 이상이어야 합니다.');
      return;
    }
    if (!Number.isFinite(validityValue) || validityValue <= 0) {
      setError('유효기간은 1 이상이어야 합니다.');
      return;
    }
    // 지급 보류 모드(결제/배송완료 후 N일)는 N≥1 필수. 0 을 보내면 BE 가 IMMEDIATE 로 강등하여
    // 운영자 의도가 조용히 사라지므로(be-user-mileage 정규화), 제출 전에 막는다.
    // 구매확정 시(AFTER_PURCHASE_CONFIRM)는 지연 일수 개념이 없어 검사 대상이 아니다.
    if (!isImmediatePayout(form.payoutMode)) {
      const days = toNumberOrNull(form.payoutDelayDays);
      if (days == null || days < 1) {
        setError('지급 보류 모드에서는 보류 일수를 1일 이상 입력하세요.');
        return;
      }
    }
    // RATE 는 운영자가 입력한 퍼센트를 wire 의 basis points 로 변환한다. FIXED 는 정액(원) 그대로.
    const earnValuePayload =
      form.earnType === 'RATE' ? percentToBasisPoints(earnValue) : Math.floor(earnValue);
    try {
      if (isNew) {
        const payload: MileageEarnPolicyCreatePayload = {
          name: form.name.trim(),
          scope: form.scope,
          earnType: form.earnType,
          earnValue: earnValuePayload,
          earnBasis: form.earnBasis,
          validityValue: Math.floor(validityValue),
          validityUnit: form.validityUnit,
          active: form.active,
          ...buildPayoutFields(),
          ...buildDefaultFields(),
        };
        await create.mutateAsync(payload);
      } else if (policyId) {
        const payload: MileageEarnPolicyUpdatePayload = {
          name: form.name.trim(),
          earnType: form.earnType,
          earnValue: earnValuePayload,
          earnBasis: form.earnBasis,
          validityValue: Math.floor(validityValue),
          validityUnit: form.validityUnit,
          active: form.active,
          ...buildPayoutFields(),
          ...buildDefaultFields(),
        };
        await update.mutateAsync({ policyId, payload });
      }
      navigate('/mileage/earn-policies');
    } catch (e) {
      setError(friendlyMessage(e, '저장에 실패했습니다.'));
    }
  };

  const isBusy = create.isPending || update.isPending || softDelete.isPending;

  const breadcrumb = [
    { label: '적립 정책', href: '/mileage/earn-policies' },
    { label: isNew ? '정책 등록' : '정책 수정' },
  ];

  if (!isNew && query.isPending) {
    return (
      <PageShell title="적립 정책 수정" breadcrumb={breadcrumb}>
        <div className="space-y-3" role="status" aria-live="polite">
          <Skeleton className="h-[44px] w-full" />
          <Skeleton className="h-[200px] w-full" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={isNew ? '적립 정책 등록' : '적립 정책 수정'}
      breadcrumb={breadcrumb}
      actions={
        <div className="flex gap-2">
          {!isNew && (
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} disabled={isBusy}>
              삭제
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => navigate('/mileage/earn-policies')} disabled={isBusy}>
            취소
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={isBusy}>
            저장
          </Button>
        </div>
      }
    >
      {error ? (
        <div className="rounded-md p-3 text-sm" style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}>
          {error}
        </div>
      ) : null}

      <SectionCard title="기본 정보">
        <div className="flex flex-col gap-3">
          <FormSection density="compact" label="정책명" required>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} maxLength={100} required aria-label="정책명" />
          </FormSection>
          <FormSection density="compact" label="범위">
            {isNew ? (
              <RadioGroup
                value={form.scope}
                onValueChange={(v) => set('scope', v as MileagePolicyScope)}
                options={SCOPE_OPTIONS}
                orientation="horizontal"
                aria-label="범위"
              />
            ) : (
              <Input value={isDefault ? '전역 기본 (DEFAULT)' : '상품별 (PRODUCT)'} readOnly aria-label="범위" />
            )}
          </FormSection>
          <FormSection density="compact" label="적립 방식">
            <RadioGroup
              value={form.earnType}
              onValueChange={(v) => set('earnType', v as MileageEarnType)}
              options={EARN_TYPE_OPTIONS}
              orientation="horizontal"
              aria-label="적립 방식"
            />
          </FormSection>
          <FormSection
            density="compact"
            required
            label={form.earnType === 'RATE' ? '적립률 (%)' : '적립 정액 (원)'}
          >
            <div className="flex flex-col gap-1">
              <Input
                type="number"
                min={form.earnType === 'RATE' ? 0 : 1}
                step={form.earnType === 'RATE' ? 0.1 : 1}
                inputMode="decimal"
                value={form.earnValue}
                onChange={(e) => set('earnValue', e.target.value)}
                placeholder={form.earnType === 'RATE' ? '예: 5 = 5% 적립' : '예: 1000 = 1,000원 적립'}
                aria-label="적립값"
              />
              <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                {earnValuePreview}
              </span>
            </div>
          </FormSection>
          <FormSection density="compact" label="적립 기준">
            <RadioGroup
              value={form.earnBasis}
              onValueChange={(v) => set('earnBasis', v as MileageEarnBasis)}
              options={BASIS_OPTIONS}
              orientation="horizontal"
              aria-label="적립 기준"
            />
          </FormSection>
          <FormSection density="compact" label="유효기간" required>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={form.validityValue}
                onChange={(e) => set('validityValue', e.target.value)}
                aria-label="유효기간 값"
                className="max-w-[120px]"
              />
              <RadioGroup
                value={form.validityUnit}
                onValueChange={(v) => set('validityUnit', v as MileageValidityUnit)}
                options={VALIDITY_UNIT_OPTIONS}
                orientation="horizontal"
                aria-label="유효기간 단위"
                className="min-h-8 items-center"
              />
            </div>
          </FormSection>
          <FormSection density="compact" label="활성 (사용 여부)">
            <div className="flex flex-col gap-1">
              <Switch checked={form.active} onCheckedChange={(v) => set('active', v)} aria-label="활성" />
              <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                {form.active
                  ? '켬: 이 정책을 실제 적립 계산에 사용합니다.'
                  : '끔: 정책을 보관만 하고 적립 계산에는 사용하지 않습니다.'}
              </span>
            </div>
          </FormSection>
        </div>
      </SectionCard>

      <SectionCard title="지급 시점 · 적립 제외 (고도몰 기준)">
        <div className="flex flex-col gap-3">
          <FormSection density="compact" label="지급 시점">
            <div className="flex flex-col gap-1">
              <RadioGroup
                value={form.payoutMode}
                onValueChange={(v) => set('payoutMode', v as MileagePayoutMode)}
                options={PAYOUT_MODE_OPTIONS}
                aria-label="지급 시점"
              />
              <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                {form.payoutMode === 'IMMEDIATE'
                  ? '결제확정(매입) 시 즉시 적립됩니다.'
                  : form.payoutMode === 'AFTER_PAYMENT_DAYS'
                    ? '결제확정 후 설정한 일수가 지나면 적립됩니다.'
                    : form.payoutMode === 'AFTER_DELIVERY_DAYS'
                      ? '배송완료 처리 후 설정한 일수가 지나면 적립됩니다. (반품 가능 기간 고려)'
                      : '구매확정 시 적립 — 고도몰 기본(근거: §2-2/02.png). 자동/수동 구매확정 시점에 적립됩니다.'}
              </span>
            </div>
          </FormSection>
          {!isImmediatePayout(form.payoutMode) ? (
            <FormSection density="compact" label="지급 지연 일수 (일)" required>
              <Input
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                value={form.payoutDelayDays}
                onChange={(e) => set('payoutDelayDays', e.target.value)}
                placeholder="예: 7 = 7일 후 적립"
                aria-label="지급 지연 일수"
                className="max-w-[200px]"
              />
            </FormSection>
          ) : null}
          {!isDefault ? (
            <FormSection density="compact" label="적립 제외 대상">
              <div className="flex flex-col gap-1">
                <Switch
                  checked={form.earnExcluded}
                  onCheckedChange={(v) => set('earnExcluded', v)}
                  aria-label="적립 제외 대상"
                />
                <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                  {form.earnExcluded
                    ? '켬: 이 정책이 연결된 상품은 적립 대상에서 제외됩니다.'
                    : '끔: 일반 적립 규칙을 적용합니다.'}
                </span>
              </div>
            </FormSection>
          ) : null}
        </div>
      </SectionCard>

      {isDefault ? (
        <SectionCard title="마일리지 사용 및 적립 계산 규칙 (전역 기본 정책에만 적용)">
          <div className="flex flex-col gap-3">
            <FormSection density="compact" label="마일리지 결제분 재적립">
              <div className="flex flex-col gap-1">
                <Switch
                  checked={form.reearnOnMileagePayment}
                  onCheckedChange={(v) => set('reearnOnMileagePayment', v)}
                  aria-label="마일리지 결제분 재적립"
                />
                <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                  {form.reearnOnMileagePayment
                    ? '켬: 마일리지로 결제한 금액에 대해서도 다시 마일리지를 적립합니다.'
                    : '끔: 마일리지로 결제한 금액은 적립 대상에서 제외하고, 현금·카드 등으로 결제한 금액만 적립합니다.'}
                </span>
              </div>
            </FormSection>
            <FormSection density="compact" label="최소 사용 보유액 (원)">
              <Input
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                value={form.minBalanceToUse}
                onChange={(e) => set('minBalanceToUse', e.target.value)}
                aria-label="최소 사용 보유액"
              />
            </FormSection>
            {/* QA-436 — 고도몰 "최소 상품구매금액 제한". 배송비는 빼고 상품 구매금액만 본다. */}
            <FormSection density="compact" label="최소 상품구매금액 (원)">
              <div className="flex flex-col gap-1.5">
                <Input
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  value={form.useMinPurchaseAmount}
                  onChange={(e) => set('useMinPurchaseAmount', e.target.value)}
                  placeholder="비우면 제한 없음"
                  aria-label="최소 상품구매금액"
                />
                <RadioGroup
                  value={form.useMinPurchaseBasis}
                  onValueChange={(v) => set('useMinPurchaseBasis', v as MileageMinPurchaseBasis)}
                  options={MIN_PURCHASE_BASIS_OPTIONS}
                  orientation="horizontal"
                  aria-label="최소 상품구매금액 기준"
                />
                <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                  {/* BE 규칙 — 비우거나 0 이면 제한 없음. */}
                  {(Number(form.useMinPurchaseAmount) || 0) <= 0
                    ? '상품 구매금액과 관계없이 마일리지를 쓸 수 있습니다.'
                    : `상품 구매금액 합계(배송비 제외, ${form.useMinPurchaseBasis === 'EXCLUDE_DISCOUNT' ? '할인 전' : '할인 후'})가 ${Math.floor(Number(form.useMinPurchaseAmount) || 0).toLocaleString('ko-KR')}원 이상일 때만 마일리지를 쓸 수 있습니다.`}
                </span>
                <span className="text-[12px]" style={{ color: 'var(--content-muted)' }} data-testid="min-purchase-help">
                  배송비는 상품 구매금액에 포함되지 않습니다. 할인 후 기준 = 쿠폰·회원·수량 할인을 뺀 상품금액입니다.
                </span>
              </div>
            </FormSection>
            <FormSection density="compact" label="주문당 최소 사용액 (원)">
              <Input
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                value={form.useMinPerOrder}
                onChange={(e) => set('useMinPerOrder', e.target.value)}
                aria-label="주문당 최소 사용액"
              />
            </FormSection>
            <FormSection density="compact" label="주문당 사용 상한 방식">
              <RadioGroup
                value={form.useMaxType}
                onValueChange={(v) => set('useMaxType', v as MileageUseMaxType | '')}
                options={USE_MAX_TYPE_OPTIONS}
                orientation="horizontal"
                aria-label="주문당 사용 상한 방식"
              />
            </FormSection>
            {form.useMaxType !== '' ? (
              <FormSection density="compact" label={form.useMaxType === 'PERCENT' ? '사용 상한 (%)' : '사용 상한 (원)'}>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  value={form.useMaxValue}
                  onChange={(e) => set('useMaxValue', e.target.value)}
                  aria-label="사용 상한 값"
                />
              </FormSection>
            ) : null}
            <FormSection density="compact" label="최소 적립 단위">
              <div className="flex flex-col gap-1">
                <RadixSelect
                  value={form.truncationDigits === '' ? '0' : form.truncationDigits}
                  onChange={(v) => set('truncationDigits', v)}
                  options={MIN_EARN_UNIT_OPTIONS}
                  aria-label="최소 적립 단위"
                />
                <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                  적립액을 이 단위로 내림(FLOOR)합니다. 예: 100원 단위면 1,234원 적립 → 1,200원.
                </span>
              </div>
            </FormSection>
          </div>
        </SectionCard>
      ) : null}

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>적립 정책 삭제</DialogTitle>
            <DialogDescription>"{form.name}" 정책을 삭제(비활성)하시겠습니까?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">취소</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={isBusy}
              onClick={() => {
                if (policyId) {
                  setConfirmDelete(false);
                  softDelete.mutate(policyId, { onSuccess: () => navigate('/mileage/earn-policies') });
                }
              }}
            >
              삭제
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
